import type { RxJsonSchema } from 'rxdb'

/**
 * EntityWatcher Document Type
 *
 * Represents a user watching/subscribing to a specific entity for notifications.
 * Polymorphic: entityType + entityId can reference any entity (work_item, document, project, etc.)
 */
export interface EntityWatcherDocument {
  id: string
  envId: string
  entityType: string
  entityId: string
  userId: string
  watchType: string // 'auto_creator' | 'auto_assignee' | 'manual'
  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for EntityWatcher
 */
export const entityWatcherSchema: RxJsonSchema<EntityWatcherDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    entityType: {
      type: 'string',
      maxLength: 50,
    },
    entityId: {
      type: 'string',
      maxLength: 36,
    },
    userId: {
      type: 'string',
      maxLength: 36,
    },
    watchType: {
      type: 'string',
      maxLength: 50,
      default: 'manual',
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'entityType',
    'entityId',
    'userId',
    'watchType',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'updatedOn',
    ['entityType', 'entityId', 'isDeleted'],
    ['userId', 'isDeleted'],
    ['envId', 'isDeleted'],
  ],
}
